import {
  useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  clearUploadCredential,
  getUploadCredential,
  redeemUploadHandle,
  setUploadCredential,
  UploadRedeemErrorCode
} from 'services/auth';
import { uploadFile } from 'services/tus/uploadFile';
import useAlert from 'utils/alert';
import { asyncForEach } from 'utils/helpers';
import { Buffer } from 'buffer';

import { DocumentRequestInfo, DocumentRequestPhase, UploadRedeemDisplay } from './types';

type Fragment = { kind: 'handle', handle: string } | { kind: 'not_yet' } | { kind: 'invalid' } | { kind: 'none' };
type UploadMode = 'none' | 'legacy' | 'credential';

const UNAUTHORIZED = 401;
const MAX_TIMEOUT_MS = 2_147_483_647;

const nowInSeconds = () => Math.round(Date.now() / 1000);

// Reads the fragment only; never decodes or validates the handle.
const readFragment = (): Fragment => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const handle = params.get('h');
  if (handle) return { kind: 'handle', handle };
  const error = params.get('error');
  if (error === 'not_yet') return { kind: 'not_yet' };
  return error !== null || params.has('h') ? { kind: 'invalid' } : { kind: 'none' };
};

// Client-side JWT payload read (no verification), same Buffer pattern as the legacy link.
const decodeJwtPayload = (jwt: string): any => {
  try {
    return JSON.parse(Buffer.from(jwt.split('.')[1], 'base64').toString());
  } catch (error) {
    return null;
  }
};

const toDisplay = (claims: any): UploadRedeemDisplay | null => {
  const valid = typeof claims?.order_id === 'string' && typeof claims.party === 'string'
    && typeof claims.exp === 'number' && Array.isArray(claims.files);
  if (!valid) return null;
  const {
    order_id, party, exp, files, name, number
  } = claims;
  return {
    order_id, party, exp, files, name: name ?? '', number: number ?? ''
  };
};

// Duck-types tus DetailedError.originalResponse.getStatus().
const httpStatus = (error: unknown): number | null => {
  const response = (error as { originalResponse?: { getStatus?: () => number } } | null)?.originalResponse;
  return typeof response?.getStatus === 'function' ? response.getStatus() : null;
};

// null = retryable: stay on confirm, keep the handle, re-enable the button.
const REDEEM_FAILURE_PHASE: Record<UploadRedeemErrorCode, DocumentRequestPhase | null> = {
  invalid: 'invalid',
  used: 'used',
  rate_limited: null,
  network: null
};

export const useDocumentRequestHooks = () => {
  const { t } = useTranslation();
  const Alert = useAlert();
  const { token } = useParams<{ token?: string }>();
  const [fragment] = useState<Fragment>(readFragment);
  const [handle, setHandle] = useState<string | null>(fragment.kind === 'handle' ? fragment.handle : null);
  const [phase, setPhase] = useState<DocumentRequestPhase>('loading');
  const [mode, setMode] = useState<UploadMode>('none');
  const [documentRequestInfo, setDocumentRequestInfo] = useState<UploadRedeemDisplay>();
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showRedeemRetry, setShowRedeemRetry] = useState(false);
  const redeemingRef = useRef(false);
  const [uploadIsLoading, setUploadIsLoading] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<{ archiveId: string, file: File}[]>([]);

  const expireCredential = () => {
    clearUploadCredential();
    setPhase('expired');
  };

  useEffect(() => {
    if (window.location.hash !== '') window.history.replaceState(null, '', window.location.pathname);
    if (fragment.kind === 'handle' || fragment.kind === 'not_yet') {
      clearUploadCredential();
      setPhase(fragment.kind === 'handle' ? 'confirm' : 'not_yet');
      return;
    }
    if (fragment.kind === 'invalid') {
      setPhase('invalid');
      return;
    }
    if (token) {
      clearUploadCredential();
      const info: DocumentRequestInfo | null = decodeJwtPayload(token);
      const now = nowInSeconds();
      const isValidDate = (info && now >= info.nbf && now <= info.exp) || false;
      if (info) setDocumentRequestInfo(info);
      setMode('legacy');
      setPhase(isValidDate ? 'upload' : 'invalid');
      return;
    }
    const stored = getUploadCredential();
    if (stored === null) {
      setPhase('invalid');
      return;
    }
    const display = toDisplay(decodeJwtPayload(stored));
    if (display && display.exp > nowInSeconds()) {
      setDocumentRequestInfo(display);
      setMode('credential');
      setPhase('upload');
      return;
    }
    clearUploadCredential();
    setPhase('used');
  }, [fragment, token]);

  // The credential is dropped as soon as its exp passes (never mid-upload).
  useEffect(() => {
    if (phase !== 'upload' || mode !== 'credential' || !documentRequestInfo || uploadIsLoading) return undefined;
    const delay = Math.min(Math.max(documentRequestInfo.exp * 1000 - Date.now(), 0), MAX_TIMEOUT_MS);
    const timer = setTimeout(expireCredential, delay);
    return () => clearTimeout(timer);
  }, [phase, mode, documentRequestInfo, uploadIsLoading]);

  const onConfirm = async () => {
    if (redeemingRef.current || handle === null) return;
    redeemingRef.current = true;
    setIsRedeeming(true);
    setShowRedeemRetry(false);
    const result = await redeemUploadHandle(handle);
    redeemingRef.current = false;
    setIsRedeeming(false);
    if (result.ok) {
      setHandle(null);
      setUploadCredential(result.token);
      const {
        order_id, party, files, name, number, exp
      } = result;
      setDocumentRequestInfo({
        order_id, party, files, name, number, exp
      });
      setMode('credential');
      setPhase('upload');
      return;
    }
    const failurePhase = REDEEM_FAILURE_PHASE[result.code];
    if (failurePhase === null) {
      setShowRedeemRetry(true);
      return;
    }
    setHandle(null);
    setPhase(failurePhase);
  };

  const onCloseWindow = () => {
    window.open('about:blank', '_self');
    window.close();
  };

  const onAddFile = ({ archiveId, file }: {archiveId: string, file: File}) => {
    setFilesToUpload((prevFilesToUpdate) => ([...prevFilesToUpdate, { archiveId, file }]));
  };

  const onRemoveFile = ({ archiveId, fileName }: { archiveId: string, fileName: string}) => {
    setFilesToUpload((prevFilesToUpdate) => prevFilesToUpdate.filter((fileToUpdate) => !(fileToUpdate.archiveId === archiveId && fileToUpdate.file.name === fileName)));
  };

  const onSendFiles = async () => {
    if (!documentRequestInfo) return;
    const credential = mode === 'credential' ? getUploadCredential() : token ?? null;
    if (credential === null || (mode === 'credential' && documentRequestInfo.exp <= nowInSeconds())) {
      expireCredential();
      return;
    }
    setUploadIsLoading(true);

    let filesUploadedCount = 0;
    let isExpired = false;
    await asyncForEach(filesToUpload, async ({ archiveId, file }) => {
      if (isExpired) return;
      try {
        // Upload credential (or legacy link JWT) is the tusd Bearer only; no session getter, no onUnauthorized.
        await uploadFile({
          file,
          archiveId,
          getToken: async () => credential,
          orderId: documentRequestInfo.order_id,
          partyId: documentRequestInfo.party,
          filename: file.name
        });
        filesUploadedCount += 1;
      } catch (error) {
        if (mode === 'credential' && httpStatus(error) === UNAUTHORIZED) {
          isExpired = true;
          return;
        }
        Alert(t('documents:error-uplading'), 'error');
      }
    });

    setUploadIsLoading(false);
    if (isExpired) {
      expireCredential();
      return;
    }
    if (filesUploadedCount === filesToUpload.length) {
      if (mode === 'credential') clearUploadCredential();
      setPhase('thanks');
    }
  };

  return {
    t,
    phase,
    documentRequestInfo,
    isRedeeming,
    showRedeemRetry,
    isSendButtonDisable: filesToUpload.length <= 0,
    onConfirm,
    onCloseWindow,
    onAddFile,
    onRemoveFile,
    uploadIsLoading,
    onSendFiles
  };
};
